import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { addDaysIso, fmtDate, startOfWeekMonIso, todayLocalIso, dayOfWeekIso } from '../../shared/dates';
import { ScheduleGrid, type Block } from './ScheduleGrid';
import { ResourceCalendarView } from './ResourceCalendarView';
import { ServiceLabels, ResourceTypeLabels } from '@census/shared';

type View = 'resource-day' | 'resource-week' | 'client-week';

export function SchedulePage() {
  const [view, setView] = useState<View>('resource-day');
  const [date, setDate] = useState<string>(todayLocalIso());
  const [resourceId, setResourceId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');

  const weekStart = startOfWeekMonIso(date);

  const { data: resources } = useQuery({
    queryKey: ['resources-active'],
    queryFn: () => api.get('/resources?activeOnly=true').then((r) => r.data),
  });
  const { data: clients } = useQuery({
    queryKey: ['clients-active'],
    queryFn: () => api.get('/clients', { params: { status: 'ACTIVE' } }).then((r) => r.data),
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Schedule</h1>
          <p className="text-sm text-ink-subtle">Capacity-aware resource calendar.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            className="input h-9 w-auto py-1.5"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date"
          />
          <Tabs value={view} onChange={setView} />
        </div>
      </header>

      {(view === 'resource-week' || view === 'client-week') && (
        <section className="card flex flex-wrap items-end gap-3 px-3 py-2">
          {view === 'resource-week' && (
            <div className="min-w-[280px]">
              <label className="label">Resource</label>
              <select className="input h-9 py-1.5" value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
                <option value="">— select —</option>
                {(resources ?? []).map((r: any) => (
                  <option key={r.id} value={r.id}>
                    [{ResourceTypeLabels[r.type as 'PROVIDER']}] {r.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {view === 'client-week' && (
            <div className="min-w-[280px]">
              <label className="label">Client</label>
              <select className="input h-9 py-1.5" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">— select —</option>
                {(clients ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.cohortId} — {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </section>
      )}

      {view === 'resource-day' && <ResourceCalendarView date={date} />}
      {view === 'resource-week' && resourceId && (
        <ResourceWeek resourceId={resourceId} weekStart={weekStart} />
      )}
      {view === 'client-week' && clientId && (
        <ClientWeek clientId={clientId} weekStart={weekStart} />
      )}
    </div>
  );
}

function Tabs({ value, onChange }: { value: View; onChange: (v: View) => void }) {
  const tabs: { v: View; label: string }[] = [
    { v: 'resource-day', label: 'Day' },
    { v: 'resource-week', label: 'Resource week' },
    { v: 'client-week', label: 'Client week' },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-md bg-surface-muted p-0.5 ring-1 ring-line">
      {tabs.map((t) => (
        <button
          key={t.v}
          onClick={() => onChange(t.v)}
          className={clsx(
            'rounded px-3 py-1.5 text-sm font-medium transition',
            value === t.v
              ? 'bg-white text-ink shadow-sm'
              : 'text-ink-muted hover:text-ink',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function ResourceWeek({ resourceId, weekStart }: { resourceId: string; weekStart: string }) {
  const { data } = useQuery({
    queryKey: ['resource-week', resourceId, weekStart],
    queryFn: () =>
      api.get(`/schedule/resource/${resourceId}`, { params: { weekStart } }).then((r) => r.data),
  });
  if (!data) return null;
  const blocks: Block[] = data.instances.map((i: any) => ({
    day: dayOfWeekIso(i.scheduledDate),
    startMin: i.startMin,
    endMin: i.endMin,
    label: ServiceLabels[i.serviceType as 'THERAPY'],
    sub: i.kind === 'GROUP' ? `Group · ${i.cohortId ?? ''}` : `${i.cohortId ?? ''} ${i.clientName ?? ''}`,
    kind: i.kind,
    status: i.status,
  })) ?? [];
  return (
    <section className="space-y-3">
      <Header>
        <div>
          <h2 className="text-lg font-semibold">{data.resource.displayName}</h2>
          <p className="text-sm text-slate-500">
            {ResourceTypeLabels[data.resource.type as 'PROVIDER']} · capacity {data.resource.capacity} ·{' '}
            week of {fmtDate(weekStart)} → {fmtDate(addDaysIso(weekStart, 6))}
          </p>
        </div>
        <div className="text-right text-sm">
          {data.weeklyCapMinutes > 0 && (
            <div>
              {(data.weeklyMinutes / 60).toFixed(1)}h / {(data.weeklyCapMinutes / 60).toFixed(0)}h ·{' '}
              <span
                className={
                  data.utilizationPct > 95
                    ? 'pill-danger'
                    : data.utilizationPct > 90
                      ? 'pill-warn'
                      : 'pill-info'
                }
              >
                {data.utilizationPct.toFixed(0)}%
              </span>
            </div>
          )}
          <div className="text-slate-500">{data.uniqueClients} unique clients</div>
        </div>
      </Header>
      <ScheduleGrid blocks={blocks} />
    </section>
  );
}

function ClientWeek({ clientId, weekStart }: { clientId: string; weekStart: string }) {
  const { data } = useQuery({
    queryKey: ['client-week', clientId, weekStart],
    queryFn: () =>
      api.get(`/schedule/client/${clientId}`, { params: { weekStart } }).then((r) => r.data),
  });
  if (!data) return null;
  const blocks: Block[] = data.instances.map((i: any) => ({
    day: dayOfWeekIso(i.scheduledDate),
    startMin: i.startMin,
    endMin: i.endMin,
    label: ServiceLabels[i.serviceType as 'THERAPY'],
    sub: i.resourceName,
    kind: i.kind,
    status: i.status,
  })) ?? [];
  return (
    <section className="space-y-3">
      <Header>
        <div>
          <h2 className="text-lg font-semibold">
            {data.client.cohortId} — {data.client.firstName} {data.client.lastName}
          </h2>
          <p className="text-sm text-slate-500">Week of {fmtDate(weekStart)}</p>
        </div>
      </Header>
      <ScheduleGrid blocks={blocks} />
    </section>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return <div className="card flex flex-wrap items-end justify-between gap-3 p-4">{children}</div>;
}
